// src/lib/access-control.ts
import type { AccessControl, Tag } from '@/lib/firebase';
import type { User } from 'firebase/auth';
import { predefinedGroups } from '@/lib/groups';

/**
 * Checks if a user has access to an assistant based on the access control configuration
 * @param accessControl - The access control configuration for the assistant
 * @param user - The current Firebase user (or null if not logged in)
 * @param ownerEmail - The email of the owner who created the assistant
 * @returns boolean - Whether the user has access
 */
export function checkAccess(
  accessControl: AccessControl | undefined,
  user: User | null,
  ownerEmail?: string
): boolean {
  // Owner/creator always has access
  if (user?.email && ownerEmail && user.email.toLowerCase() === ownerEmail.toLowerCase()) {
    return true;
  }

  // If no access control is defined, default to private access
  if (!accessControl) {
    return false;
  }

  // Check based on access type
  switch (accessControl.type) {
    case 'public':
      // Everyone has access
      return true;
      
    case 'private':
      // Only owner has access, already checked above
      return false;
      
    case 'domain':
      // Check if the user is logged in and has the same domain as specified
      if (!user?.email) {
        return false;
      }
      
      const userDomain = user.email.split('@')[1]?.toLowerCase();
      
      // For domain-based access control, check against the specified domain in accessControl
      // If no domain is specified in accessControl, fall back to comparing with owner email domain
      const targetDomain = accessControl.domain?.toLowerCase() || ownerEmail?.split('@')[1]?.toLowerCase();

      // Check if domains could be extracted
      if (!userDomain || !targetDomain) {
        return false;
     }
      
      return userDomain === targetDomain;
    
    case 'domains':
      // Check if the user's domain is in the list of allowed domains
      if (!user?.email || !accessControl.domains || accessControl.domains.length === 0) {
        return false;
      }
      
      const userEmailDomain = user.email.split('@')[1]?.toLowerCase();
      
      return accessControl.domains.some(
        domain => domain.toLowerCase() === userEmailDomain
      );
      
    case 'specific':
      // Check if the user is in the list of allowed emails
      if (!user?.email || !accessControl.emails || accessControl.emails.length === 0) {
        return false;
      }
      
      return accessControl.emails.some(
        email => email.toLowerCase() === user.email?.toLowerCase()
      );
      
    case 'group':
      // Check if the user is in any of the allowed groups
      if (!user?.email || !accessControl.groups || accessControl.groups.length === 0) {
        return false;
      }
      
      // Check each group for membership
      return accessControl.groups.some(groupId => {
        const group = predefinedGroups.find(g => g.id === groupId);
        if (!group || !group.emails) return false;
        
        return group.emails.some(
          email => email.toLowerCase() === user.email?.toLowerCase()
        );
      });
      
    default:
      // Default to no access for unknown access types
      return false;
  }
}

/**
 * Returns an appropriate message for users who don't have access
 * @param accessControl - The access control configuration for the assistant
 * @param isLoggedIn - Whether the user is logged in
 * @param ownerEmail - The email of the owner who created the assistant
 * @returns string - A message explaining why access is denied
 */
export function getAccessDeniedMessage(
  accessControl: AccessControl | undefined,
  isLoggedIn: boolean,
  ownerEmail?: string
): string {
  // Default to private message if no access control
  if (!accessControl) {
    return "This assistant is private and only accessible to its owner.";
  }

  switch (accessControl.type) {
    case 'private':
      return "This assistant is private and only accessible to its owner.";
      
    case 'domain':
      if (!isLoggedIn) {
        return "You need to log in to access this assistant.";
      }
      
      const domain = accessControl.domain || ownerEmail?.split('@')[1] || '';
      return `This assistant is only available to users with @${domain} email addresses.`;
    
    case 'domains':
      if (!isLoggedIn) {
        return "You need to log in to access this assistant.";
      }
      
      const domainList = accessControl.domains?.join(', @') || '';
      return `This assistant is only available to users with email addresses from the following domains: @${domainList}.`;
      
    case 'specific':
      if (!isLoggedIn) {
        return "You need to log in to access this assistant.";
      }
      
      return "This assistant is only available to specific users.";
      
    case 'group':
      if (!isLoggedIn) {
        return "You need to log in to access this assistant.";
      }
      
      return "This assistant is only available to members of specific groups.";
      
    case 'public':
      // This shouldn't normally happen since public assistants are accessible to everyone
      return "You don't have access to this assistant.";
      
    default:
      return "You don't have access to this assistant.";
  }
}

/**
 * Gets all tags that a user qualifies for (is assigned to)
 * @param availableTags - Array of all available tags with their access controls
 * @param user - The current Firebase user (or null if not logged in)
 * @returns Array of tag IDs that the user is assigned to
 */
export function getUserAssignedTags(
  availableTags: Tag[],
  user: User | null
): string[] {
  if (!user) {
    return [];
  }

  return availableTags
    .filter(tag => checkAccess(tag.accessControl, user, tag.createdBy))
    .map(tag => tag.id);
}

/**
 * Checks if a user has access to resources with specific tags
 * @param requiredTags - Array of tag names or IDs required for the resource
 * @param availableTags - Array of all available tags with their access controls
 * @param user - The current Firebase user (or null if not logged in)
 * @param resourceOwnerEmail - The email of the owner who created the resource
 * @returns boolean - Whether the user has access to the tagged resource
 */
export function checkTagAccess(
  requiredTags: string[] | undefined,
  availableTags: Tag[],
  user: User | null,
  resourceOwnerEmail?: string
): boolean {
  // If no tags required, allow access (tags are for additional restriction)
  if (!requiredTags || requiredTags.length === 0) {
    return true;
  }

  // Owner always has access to their own resources
  if (user?.email && resourceOwnerEmail && user.email.toLowerCase() === resourceOwnerEmail.toLowerCase()) {
    return true;
  }

  // Get all tags the user is assigned to (by ID)
  const userAssignedTagIds = getUserAssignedTags(availableTags, user);
  
  // Also get tag names for the user's assigned tags
  const userAssignedTagNames = availableTags
    .filter(tag => userAssignedTagIds.includes(tag.id))
    .map(tag => tag.name);
  
  // Check if user has at least one of the required tags (by name or ID)
  return requiredTags.some(requiredTag => 
    userAssignedTagIds.includes(requiredTag) || userAssignedTagNames.includes(requiredTag)
  );
}

/**
 * Filters a list of resources based on tag permissions
 * @param resources - Array of resources with tags
 * @param availableTags - Array of all available tags with their access controls
 * @param user - The current Firebase user (or null if not logged in)
 * @returns Array of resources the user has access to
 */
export function filterResourcesByTagAccess<T extends { tags?: string[]; ownerEmail?: string }>(
  resources: T[],
  availableTags: Tag[],
  user: User | null
): T[] {
  return resources.filter(resource => 
    checkTagAccess(resource.tags, availableTags, user, resource.ownerEmail)
  );
}

/**
 * Gets a list of tags the user has access to
 * @param tags - Array of all available tags
 * @param user - The current Firebase user (or null if not logged in)
 * @returns Array of tags the user can access
 */
export function getAccessibleTags(
  tags: Tag[],
  user: User | null
): Tag[] {
  return tags.filter(tag => checkAccess(tag.accessControl, user, tag.createdBy));
}

/**
 * Checks if an email address would have access to an assistant based on access control
 * @param email - The email address to check
 * @param accessControl - The access control configuration for the assistant
 * @param ownerEmail - The email of the owner who created the assistant
 * @returns object with hasAccess boolean and reason string
 */
export function checkEmailAccess(
  email: string,
  accessControl: AccessControl | undefined,
  ownerEmail?: string
): { hasAccess: boolean; reason?: string } {
  if (!email || !email.includes('@')) {
    return { hasAccess: false, reason: 'Invalid email address' };
  }

  const emailLower = email.toLowerCase();
  
  // Owner/creator always has access
  if (ownerEmail && emailLower === ownerEmail.toLowerCase()) {
    return { hasAccess: true };
  }

  // If no access control is defined, default to private access
  if (!accessControl) {
    return { hasAccess: false, reason: 'This assistant is private and only accessible to its owner.' };
  }

  // Check based on access type
  switch (accessControl.type) {
    case 'public':
      // Everyone has access
      return { hasAccess: true };
      
    case 'private':
      // Only owner has access
      return { hasAccess: false, reason: 'This assistant is private and only accessible to its owner.' };
      
    case 'domain':
      const userDomain = email.split('@')[1]?.toLowerCase();
      const targetDomain = accessControl.domain?.toLowerCase() || ownerEmail?.split('@')[1]?.toLowerCase();
      
      if (!userDomain || !targetDomain) {
        return { hasAccess: false, reason: 'Unable to verify domain access.' };
      }
      
      if (userDomain === targetDomain) {
        return { hasAccess: true };
      } else {
        return { hasAccess: false, reason: `This assistant is only available to users with @${targetDomain} email addresses.` };
      }
    
    case 'domains':
      if (!accessControl.domains || accessControl.domains.length === 0) {
        return { hasAccess: false, reason: 'No domains configured for access.' };
      }
      
      const emailDomain = email.split('@')[1]?.toLowerCase();
      const allowedDomains = accessControl.domains.map(d => d.toLowerCase());
      
      if (allowedDomains.includes(emailDomain)) {
        return { hasAccess: true };
      } else {
        const domainList = accessControl.domains.join(', @');
        return { hasAccess: false, reason: `This assistant is only available to users with email addresses from: @${domainList}` };
      }
      
    case 'specific':
      if (!accessControl.emails || accessControl.emails.length === 0) {
        return { hasAccess: false, reason: 'No specific emails configured for access.' };
      }
      
      const allowedEmails = accessControl.emails.map(e => e.toLowerCase());
      
      if (allowedEmails.includes(emailLower)) {
        return { hasAccess: true };
      } else {
        return { hasAccess: false, reason: 'This email address is not in the list of allowed users.' };
      }
      
    case 'group':
      // For email validation, we can't easily check group membership without the user object
      // So we'll just return a generic message
      return { hasAccess: false, reason: 'Group access validation requires login. This assistant is only available to members of specific groups.' };
      
    default:
      return { hasAccess: false, reason: 'Unknown access control type.' };
  }
}